import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useCategoryGame } from '../contexts/GameContext';
import { useToastNotification } from '../contexts/ToastContext';
import { GameResult } from '../types/game';

interface UseGameLogicProps {
  stockOptions?: string[];
}

/**
 * Custom hook for managing game logic and state
 * Extracted from GamePage to improve maintainability
 */
export const useGameLogic = (props?: UseGameLogicProps) => {
  // Memoize stockOptions to prevent unnecessary re-renders
  const stockOptions = useMemo(() => props?.stockOptions || [], [props?.stockOptions]);
  // Get game context
  const {
    currentCategory,
    gameState,
    submitGuess,
    resetGame,
    updateGameState,
    updateStockData,
    validateGuess,
    getGameStatistics,
    shareGame,
    initializeGame,
  } = useCategoryGame();

  // Toast notification
  const { showToast } = useToastNotification();

  // Local UI state
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [guessedLetters, setGuessedLetters] = useState<Map<string, 'correct' | 'present' | 'absent'>>(new Map());
  const [isShaking, setIsShaking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track mounted state to prevent state updates on unmounted components
  const isMountedRef = useRef(true);
  const timeoutIdsRef = useRef<number[]>([]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Clear all pending timeouts
      timeoutIdsRef.current.forEach(id => clearTimeout(id));
      timeoutIdsRef.current = [];
    };
  }, []);

  // Safe state updater that checks if component is mounted
  const safeSetTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      if (isMountedRef.current) {
        callback();
      }
      // Remove this timeout from tracking
      timeoutIdsRef.current = timeoutIdsRef.current.filter(id => id !== timeoutId);
    }, delay);
    timeoutIdsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  // Haptic feedback helper
  const triggerHapticFeedback = useCallback((type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30]
      };
      navigator.vibrate(patterns[type]);
    }
  }, []);

  // Handle guess submission with error handling and animations
  const handleGuessSubmit = useCallback(async (guess: string) => {
    if (isSubmitting) return; // Prevent double submissions

    const validation = validateGuess(guess, stockOptions);
    if (!validation.isValid) {
      // Show error feedback with toast and shake animation
      if (validation.error) {
        showToast(validation.error);
      }
      setIsShaking(true);

      // Reset shake animation after it completes
      safeSetTimeout(() => setIsShaking(false), 500);
      return;
    }

    setIsSubmitting(true);
    setIsRevealing(true);

    try {
      await submitGuess(guess);
    } catch (error) {
      console.error('Error submitting guess:', error);
      // Show error feedback with shake animation for submission errors too
      setIsShaking(true);
      safeSetTimeout(() => setIsShaking(false), 500);
      showToast('Error submitting guess. Please try again.');
    } finally {
      setIsSubmitting(false);
      // Reset revealing state after animation
      safeSetTimeout(() => setIsRevealing(false), 1000);
    }
  }, [validateGuess, submitGuess, isSubmitting, stockOptions, safeSetTimeout, showToast]);

  // Handle share functionality
  const handleShare = useCallback(() => {
    const shareText = shareGame();
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobile && navigator.share) {
      navigator.share({
        title: 'Wallstreetle',
        text: shareText,
      }).catch(() => {
        navigator.clipboard.writeText(shareText);
        showToast('Results copied to clipboard!');
      });
    } else {
      navigator.clipboard.writeText(shareText);
      showToast('Results copied to clipboard!');
    }
  }, [shareGame, showToast]);

  // Handle retry
  const handleRetry = useCallback(() => {
    initializeGame();
  }, [initializeGame]);

  // Handle error boundary reset
  const handleErrorBoundaryReset = useCallback(() => {
    resetGame();
    initializeGame();
  }, [resetGame, initializeGame]);

  // Generate game result when game completes
  const generateGameResult = useCallback(() => {
    if (gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') {
      const result: GameResult = {
        isWin: gameState.gameStatus === 'won',
        attempts: gameState.guesses.length,
        solution: gameState.solution?.Symbol || '',
        guesses: gameState.guesses.map(g => g.company),
        timestamp: new Date()
      };
      setGameResult(result);
      return result;
    }
    return null;
  }, [gameState.gameStatus, gameState.guesses, gameState.solution]);

  // Get game statistics
  const statistics = useMemo(() => getGameStatistics(), [getGameStatistics]);

  // Clear error state
  const clearError = useCallback(() => {
    updateGameState({ error: null });
  }, [updateGameState]);

  // Animation state helpers
  const triggerShake = useCallback(() => {
    setIsShaking(true);
    triggerHapticFeedback('heavy');
    safeSetTimeout(() => setIsShaking(false), 500);
  }, [triggerHapticFeedback, safeSetTimeout]);

  return {
    // Game state
    gameState,
    currentCategory,
    gameResult,
    isRevealing,
    guessedLetters,
    isShaking,
    isSubmitting,
    statistics,

    // Actions
    handleGuessSubmit,
    handleShare,
    handleRetry,
    handleErrorBoundaryReset,
    generateGameResult,
    triggerHapticFeedback,
    clearError,
    triggerShake,
    updateGameState,
    updateStockData,
    resetGame,
    initializeGame,

    // State setters (for component control)
    setGameResult,
    setIsRevealing,
    setGuessedLetters,
    setIsShaking,
    setIsSubmitting,
  };
};
